import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Promise<any> | undefined} */
  #cruxMlpProductPromise;

  /** @type {AbortController | undefined} */
  #cruxEyeballVariantAbortController;

  /** @type {number} */
  #cruxEyeballVariantRequestId = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#cruxSortEyeballLengthOptions();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.syncUnavailableOptions();
    this.correctUnavailableSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#cruxEyeballVariantAbortController?.abort();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    if (this.#cruxHandleMlpVariantSelection(event.target, selectedOption)) return;
    if (this.#cruxHandleEyeballVariantSelection(event.target, selectedOption)) return;
    if (this.isUnavailableOption(selectedOption)) {
      event.preventDefault();
      this.correctUnavailableSelection();
      return;
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  #cruxIsMlpPicker() {
    return this.dataset.productUrl?.split('?')[0] === '/products/mlp-pinkie-pie-series-ear-accessories-set';
  }

  #cruxMlpOptionGroups() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    if (fieldsets.length) {
      return fieldsets.map((fieldset) => ({
        type: 'fieldset',
        element: fieldset,
        controls: Array.from(fieldset.querySelectorAll('input')),
      }));
    }

    return Array.from(this.querySelectorAll('select')).map((select) => ({
      type: 'select',
      element: select,
      controls: Array.from(select.options),
    }));
  }

  #cruxMlpNormalizeOptionValue(value) {
    return String(value ?? '')
      .replace(/\s+-\s+Unavailable\s*$/i, '')
      .replace(/\s+-\s+Sold out\s*$/i, '')
      .trim();
  }

  #cruxMlpControlValue(control) {
    if (control instanceof HTMLOptionElement) {
      return this.#cruxMlpNormalizeOptionValue(control.textContent.trim() || control.value);
    }
    return this.#cruxMlpNormalizeOptionValue(control.value);
  }

  #cruxMlpSelectedValues() {
    return this.#cruxMlpOptionGroups().map((group) => {
      if (group.type === 'select') {
        const selected = group.element.options[group.element.selectedIndex];
        return selected ? this.#cruxMlpControlValue(selected) : '';
      }

      const checked = group.element.querySelector('input:checked');
      return checked ? checked.value : '';
    });
  }

  #cruxMlpChangedIndex(target) {
    if (target instanceof HTMLSelectElement) {
      return Array.from(this.querySelectorAll('select')).indexOf(target);
    }

    return Number.isNaN(Number(target?.dataset?.fieldsetIndex)) ? -1 : Number(target.dataset.fieldsetIndex);
  }

  #cruxMlpChangedValue(target, selectedOption) {
    if (target instanceof HTMLSelectElement) {
      const selected = target.options[target.selectedIndex];
      return selected ? this.#cruxMlpControlValue(selected) : target.value;
    }

    return selectedOption instanceof HTMLElement ? this.#cruxMlpControlValue(selectedOption) : '';
  }

  #cruxLoadMlpProduct() {
    if (!this.#cruxMlpProductPromise) {
      this.#cruxMlpProductPromise = fetch('/products/mlp-pinkie-pie-series-ear-accessories-set.js')
        .then((response) => response.json())
        .catch(() => null);
    }

    return this.#cruxMlpProductPromise;
  }

  #cruxMlpVariantOptions(variant) {
    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  #cruxSyncMlpMediaAfterFeaturedMorph(resource, morphElementSelector) {
    if (!this.#cruxIsMlpPicker() || morphElementSelector !== 'featured-product-information') return;

    const selectedMediaId =
      resource?.featured_media?.id?.toString?.() ||
      resource?.featured_image?.id?.toString?.();

    if (!selectedMediaId) return;

    requestAnimationFrame(() => {
      const newPicker = document.querySelector(
        'featured-product-information variant-picker[data-product-url*="mlp-pinkie-pie-series-ear-accessories-set"]'
      );
      const featuredProduct = newPicker?.closest('featured-product-information');
      const gallery = featuredProduct?.querySelector('media-gallery');
      if (!gallery) return;

      const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
      const targetIndex = slides.findIndex((slide) => {
        const media = slide.querySelector('.product-media[data-media-id]');
        return media?.dataset.mediaId === selectedMediaId;
      });

      if (targetIndex < 0) return;
      gallery.slideshow?.select(targetIndex, undefined, { animate: false });
    });
  }

  #cruxChooseMlpCandidate(product, values, lockedIndex, lockedValue) {
    const available = product.variants.filter((variant) => variant.available);
    if (!available.length) return null;

    let candidates = available;
    const lockedProductValue = lockedIndex === 0 ? lockedValue : values[0];
    if (lockedProductValue) {
      const sameProduct = available.filter((variant) => this.#cruxMlpVariantOptions(variant)[0] === lockedProductValue);
      if (sameProduct.length) candidates = sameProduct;
    }

    if (lockedIndex > 0 && lockedValue) {
      const sameLockedValue = candidates.filter(
        (variant) => this.#cruxMlpVariantOptions(variant)[lockedIndex] === lockedValue
      );
      if (sameLockedValue.length) candidates = sameLockedValue;
    }

    return candidates
      .slice()
      .sort((a, b) => {
        const aOptions = this.#cruxMlpVariantOptions(a);
        const bOptions = this.#cruxMlpVariantOptions(b);
        const aScore = aOptions.reduce((score, option, index) => score + (option === values[index] ? 1 : 0), 0);
        const bScore = bOptions.reduce((score, option, index) => score + (option === values[index] ? 1 : 0), 0);
        return bScore - aScore;
      })[0];
  }

  #cruxForceControlAvailable(control) {
    if (control instanceof HTMLOptionElement) {
      control.disabled = false;
      control.dataset.optionAvailable = 'true';
      control.setAttribute('aria-disabled', 'false');
      return;
    }

    if (control instanceof HTMLInputElement) {
      control.disabled = false;
      control.removeAttribute('disabled');
      control.dataset.optionAvailable = 'true';
      control.setAttribute('aria-disabled', 'false');
      control.closest('label')?.removeAttribute('aria-disabled');
    }
  }

  #cruxSetMlpGroupValue(group, value, variantId) {
    if (group.type === 'select') {
      const option = group.controls.find((candidate) => {
        return (
          this.#cruxMlpControlValue(candidate) === value ||
          this.#cruxMlpNormalizeOptionValue(candidate.value) === value
        );
      });
      if (!option) return null;
      this.#cruxForceControlAvailable(option);
      option.dataset.variantId = String(variantId);
      for (const candidate of group.controls) {
        candidate.removeAttribute('selected');
      }
      option.setAttribute('selected', 'selected');
      group.element.value = option.value;
      return group.element;
    }

    const input = group.controls.find((candidate) => candidate.value === value);
    if (!input) return null;
    this.#cruxForceControlAvailable(input);
    input.dataset.variantId = String(variantId);
    input.checked = true;
    input.dataset.currentChecked = 'true';
    return input;
  }

  #cruxApplyMlpCandidate(candidate, preferredTriggerIndex) {
    const groups = this.#cruxMlpOptionGroups();
    const options = this.#cruxMlpVariantOptions(candidate);
    let trigger = null;

    options.forEach((value, index) => {
      const changed = this.#cruxSetMlpGroupValue(groups[index], value, candidate.id);
      if (changed && (trigger === null || index === preferredTriggerIndex || index > preferredTriggerIndex)) {
        trigger = changed;
      }
    });

    return trigger;
  }

  #cruxHandleMlpVariantSelection(target, selectedOption) {
    if (!this.#cruxIsMlpPicker() || this.dataset.cruxMlpNativeRepairing === 'true') return false;
    if (!(target instanceof HTMLElement)) return false;

    const lockedIndex = this.#cruxMlpChangedIndex(target);
    const lockedValue = this.#cruxMlpChangedValue(target, selectedOption);
    if (lockedIndex < 0 || !lockedValue) return false;

    const values = this.#cruxMlpSelectedValues();
    values[lockedIndex] = lockedValue;
    this.dataset.cruxMlpNativeRepairing = 'loading';

    this.#cruxLoadMlpProduct().then((product) => {
      if (!product?.variants?.length || !document.documentElement.contains(this)) return;

      const latestValues = this.#cruxMlpSelectedValues();
      latestValues[lockedIndex] = lockedValue;
      const candidate = this.#cruxChooseMlpCandidate(product, latestValues, lockedIndex, lockedValue);
      if (!candidate) return;

      const trigger = this.#cruxApplyMlpCandidate(candidate, lockedIndex) || target;
      this.dataset.cruxMlpNativeRepairing = 'true';
      requestAnimationFrame(() => {
        trigger.dispatchEvent(new Event('input', { bubbles: true }));
        trigger.dispatchEvent(new Event('change', { bubbles: true }));
        setTimeout(() => {
          delete this.dataset.cruxMlpNativeRepairing;
        }, 800);
      });
    }).catch((error) => {
      console.error(error);
    }).finally(() => {
      if (this.dataset.cruxMlpNativeRepairing === 'loading') {
        delete this.dataset.cruxMlpNativeRepairing;
      }
    });

    return true;
  }

  #cruxEyeballOptionGroups(root = this) {
    const fieldsets = Array.from(root.querySelectorAll('fieldset'));
    if (fieldsets.length) {
      return fieldsets.map((fieldset) => ({
        type: 'fieldset',
        element: fieldset,
        label: this.#cruxOptionGroupLabel(fieldset),
        controls: Array.from(fieldset.querySelectorAll('input')),
      }));
    }

    return Array.from(root.querySelectorAll('select')).map((select) => ({
      type: 'select',
      element: select,
      label: this.#cruxOptionGroupLabel(select.closest('.variant-option') || select.parentElement || select),
      controls: Array.from(select.options),
    }));
  }

  #cruxOptionGroupLabel(element) {
    const explicit = element?.querySelector?.('legend, label, .variant-option__legend, .form__label');
    const text = explicit?.textContent || element?.textContent || '';
    return this.#cruxNormalizeEyeballValue(text).toLowerCase();
  }

  #cruxIsEyeballThreeOptionPicker() {
    if (document.body?.dataset?.cruxWorld !== 'dark') return false;
    if (this.dataset.cruxMlpNativeRepairing === 'true') return false;
    const productUrl = this.dataset.productUrl || '';
    const groups = this.#cruxEyeballOptionGroups();
    if (groups.length < 2) return false;

    const labels = groups.map((group) => group.label);
    const gaugeIndex = groups.length - 2;
    const lengthIndex = groups.length - 1;
    const gaugeLooksCorrect = /gauge|bar style/i.test(labels[gaugeIndex]);
    const lengthLooksCorrect = /bar length|barbell length|length/i.test(labels[lengthIndex]);
    if (groups.length === 2) return gaugeLooksCorrect && lengthLooksCorrect;

    const firstLooksLikeEye = /\b(eye|eyeball)\b/i.test(labels[0]) || /cthulhu-eye|eyeball/i.test(productUrl);
    return firstLooksLikeEye && gaugeLooksCorrect && lengthLooksCorrect;
  }

  #cruxNormalizeEyeballValue(value) {
    return String(value ?? '')
      .replace(/\s+-\s+Unavailable\s*$/i, '')
      .replace(/\s+-\s+Sold out\s*$/i, '')
      .trim();
  }

  #cruxSortEyeballLengthOptions() {
    if (!this.#cruxIsEyeballThreeOptionPicker()) return;
    const lengthGroup = this.#cruxEyeballOptionGroups().at(-1);
    if (!lengthGroup) return;

    const rank = (control, fallback) => {
      const match = this.#cruxEyeballControlValue(control).match(/(\d+(?:\.\d+)?)\s*mm/i);
      return match ? Number(match[1]) : 1000 + fallback;
    };

    if (lengthGroup.type === 'select') {
      const currentValue = lengthGroup.element.value;
      lengthGroup.controls
        .map((control, index) => ({ control, index }))
        .sort((left, right) => rank(left.control, left.index) - rank(right.control, right.index))
        .forEach(({ control }, index) => {
          control.dataset.inputIndex = String(index);
          lengthGroup.element.append(control);
        });
      lengthGroup.element.value = currentValue;
      return;
    }

    lengthGroup.controls
      .map((control, index) => ({ control, index, label: control.closest('label') }))
      .filter((item) => item.label)
      .sort((left, right) => rank(left.control, left.index) - rank(right.control, right.index))
      .forEach(({ control, label }, index) => {
        control.dataset.inputIndex = String(index);
        lengthGroup.element.append(label);
      });
  }

  #cruxEyeballControlValue(control) {
    if (control instanceof HTMLOptionElement) {
      return this.#cruxNormalizeEyeballValue(control.textContent.trim() || control.value);
    }
    return this.#cruxNormalizeEyeballValue(control?.value);
  }

  #cruxEyeballSelectedControl(group) {
    if (!group) return undefined;
    if (group.type === 'select') return group.element.options[group.element.selectedIndex];
    return group.controls.find((control) => control.checked || control.hasAttribute('checked'));
  }

  #cruxEyeballChangedIndex(target) {
    if (target instanceof HTMLSelectElement) return Array.from(this.querySelectorAll('select')).indexOf(target);
    return Number.isNaN(Number(target?.dataset?.fieldsetIndex)) ? -1 : Number(target.dataset.fieldsetIndex);
  }

  #cruxEyeballChangedValue(target, selectedOption) {
    if (target instanceof HTMLSelectElement) {
      const selected = target.options[target.selectedIndex];
      return selected ? this.#cruxEyeballControlValue(selected) : target.value;
    }
    return selectedOption instanceof HTMLElement ? this.#cruxEyeballControlValue(selectedOption) : '';
  }

  #cruxEyeballVariantOptions(variant) {
    if (Array.isArray(variant?.options) && variant.options.length) return variant.options.filter(Boolean);
    return [variant?.option1, variant?.option2, variant?.option3].filter(Boolean);
  }

  #cruxEyeballCurrentResource() {
    const text = this.querySelector('script[type="application/json"]')?.textContent;
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (_error) {
      return null;
    }
  }

  #cruxEyeballSetGroupControl(group, control) {
    if (!group || !control) return;
    if (group.type === 'select') {
      for (const candidate of group.controls) {
        const selected = candidate === control;
        candidate.selected = selected;
        candidate.toggleAttribute('selected', selected);
      }
      group.element.value = control.value;
      return;
    }

    for (const candidate of group.controls) {
      const selected = candidate === control;
      candidate.checked = selected;
      candidate.toggleAttribute('checked', selected);
      candidate.dataset.currentChecked = selected ? 'true' : 'false';
    }
  }

  #cruxEyeballSetGroupValue(group, value) {
    const control = group?.controls.find((candidate) => this.#cruxEyeballControlValue(candidate) === value);
    if (!control) return false;
    this.#cruxEyeballSetGroupControl(group, control);
    return true;
  }

  #cruxEyeballControlAvailable(control) {
    return Boolean(
      control &&
      control.dataset.optionAvailable !== 'false' &&
      control.getAttribute('aria-disabled') !== 'true'
    );
  }

  #cruxEyeballSelectedOptionValueIds(root) {
    return this.#cruxEyeballOptionGroups(root)
      .map((group) => this.#cruxEyeballSelectedControl(group)?.dataset?.optionValueId || '')
      .filter(Boolean);
  }

  #cruxEyeballRequestUrlForOptionValueIds(optionValueIds) {
    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl || optionValueIds.length < 2) return null;
    const params = ['option_values=' + optionValueIds.join(',')];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.unshift('view=' + viewParamValue);
    return productUrl + '?' + params.join('&');
  }

  async #cruxFetchEyeballSection(requestUrl, signal) {
    const response = await fetch(requestUrl, { signal });
    if (!response.ok) throw new Error('Eyeball variant request failed: ' + response.status);
    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
    const text = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!text) throw new Error('Eyeball variant response has no resource');
    return { html, resource: JSON.parse(text) };
  }

  #cruxEyeballResponseMatchesLock(resource, lockedIndex, lockedValue) {
    const options = this.#cruxEyeballVariantOptions(resource);
    return Boolean(resource?.id && resource?.available !== false && options[lockedIndex] === lockedValue);
  }

  #cruxEyeballBuildFallbackRequest(html, lockedIndex, lockedValue) {
    const picker = html.querySelector('variant-picker');
    if (!picker) return null;
    const groups = this.#cruxEyeballOptionGroups(picker);
    if (groups.length < 2) return null;

    const lengthIndex = groups.length - 1;
    const lockedControl = groups[lockedIndex]?.controls.find(
      (control) => this.#cruxEyeballControlValue(control) === lockedValue
    );
    if (!lockedControl || !this.#cruxEyeballControlAvailable(lockedControl)) return null;
    this.#cruxEyeballSetGroupControl(groups[lockedIndex], lockedControl);

    if (lockedIndex !== lengthIndex) {
      const lengthGroup = groups[lengthIndex];
      const selectedLength = this.#cruxEyeballSelectedControl(lengthGroup);
      if (!this.#cruxEyeballControlAvailable(selectedLength) || !selectedLength?.dataset?.variantId) {
        const firstAvailableLength = lengthGroup.controls.find(
          (control) => this.#cruxEyeballControlAvailable(control) && control.dataset.variantId
        );
        if (!firstAvailableLength) return null;
        this.#cruxEyeballSetGroupControl(lengthGroup, firstAvailableLength);
      }
    }

    return this.#cruxEyeballRequestUrlForOptionValueIds(this.#cruxEyeballSelectedOptionValueIds(picker));
  }

  #cruxEyeballSection() {
    return this.closest('.shopify-section, dialog, product-card');
  }

  #cruxEyeballSetPurchaseBusy(busy, purchaseStateValid = false) {
    const section = this.#cruxEyeballSection();
    if (!section) return;
    section.toggleAttribute('data-crux-eyeball-variant-busy', busy);
    for (const button of section.querySelectorAll('product-form-component button[type="submit"]')) {
      if (busy) button.setAttribute('disabled', 'disabled');
      else if (purchaseStateValid) button.removeAttribute('disabled');
    }
    for (const accelerated of section.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout')) {
      if (busy) {
        accelerated.setAttribute('inert', '');
        accelerated.setAttribute('aria-busy', 'true');
      } else if (purchaseStateValid) {
        accelerated.removeAttribute('inert');
        accelerated.setAttribute('aria-busy', 'false');
      }
    }
  }

  #cruxEyeballSyncPurchaseVariant(variantId) {
    const section = this.#cruxEyeballSection();
    if (!section || !variantId) return;
    for (const input of section.querySelectorAll('form[action*="/cart/add"] input[name="id"]')) {
      input.value = String(variantId);
      input.setAttribute('value', String(variantId));
    }
  }

  #cruxSyncEyeballCampaignPrice(resource) {
    // CRUX: sync active Cthulhu picker campaign price v1
    const section = this.#cruxEyeballSection();
    const original = section?.querySelector('product-price [data-crux-original-price]');
    const discounted = section?.querySelector('product-price [data-crux-discount-price]');
    const badge = section?.querySelector('product-price [data-crux-discount-badge]');
    const cents = Number(resource?.price);
    const percentage = Number((badge?.textContent || '').match(/(\d+(?:\.\d+)?)\s*%/)?.[1]);
    if (!original || !discounted || !Number.isFinite(cents) || !Number.isFinite(percentage)) return;

    const formatLike = (sample, amount) => sample.replace(/-?[\d,.]+/, (amount / 100).toFixed(2));
    original.textContent = formatLike(original.textContent || '$0.00', cents);
    discounted.textContent = formatLike(discounted.textContent || '$0.00', Math.ceil(cents * (100 - percentage) / 100));
  }

  #cruxEyeballDispatchVariantUpdate(resource, html, newProduct) {
    const section = this.#cruxEyeballSection();
    const selectedOptionValueId = this.#cruxEyeballSelectedOptionValueIds(this).at(-1);
    if (!section || !selectedOptionValueId) return false;
    section.dispatchEvent(
      new VariantUpdateEvent(resource, selectedOptionValueId, {
        html,
        productId: this.dataset.productId ?? '',
        newProduct,
      })
    );
    return true;
  }

  #cruxCommitEyeballVariant(html, resource) {
    const newProduct = this.updateVariantPicker(html);
    this.#cruxEyeballSyncPurchaseVariant(resource.id);
    this.#cruxSyncEyeballCampaignPrice(resource);
    if (!this.#cruxEyeballDispatchVariantUpdate(resource, html, newProduct)) {
      throw new Error('Eyeball variant update could not reach the product section');
    }

    if (this.dataset.templateProductMatch === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(resource.id));
      if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
    }
  }

  #cruxRestoreEyeballVariant(resource) {
    if (!resource?.id) return;
    const groups = this.#cruxEyeballOptionGroups();
    this.#cruxEyeballVariantOptions(resource).forEach((value, index) => {
      this.#cruxEyeballSetGroupValue(groups[index], value);
    });
    this.#cruxEyeballSyncPurchaseVariant(resource.id);
    this.#cruxEyeballDispatchVariantUpdate(resource, document, undefined);
  }

  #cruxSyncEyeballMediaAfterFeaturedMorph(resource, morphElementSelector) {
    if (!this.#cruxIsEyeballThreeOptionPicker() || morphElementSelector !== 'featured-product-information') return;
    const selectedMediaId = resource?.featured_media?.id?.toString?.() || resource?.featured_image?.id?.toString?.();
    if (!selectedMediaId) return;
    requestAnimationFrame(() => {
      const featuredProduct = this.closest('featured-product-information') || document.querySelector('featured-product-information');
      const gallery = featuredProduct?.querySelector('media-gallery');
      if (!gallery) return;
      const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
      const targetIndex = slides.findIndex((slide) => {
        const media = slide.querySelector('.product-media[data-media-id]');
        return media?.dataset.mediaId === selectedMediaId;
      });
      if (targetIndex >= 0) gallery.slideshow?.select(targetIndex, undefined, { animate: false });
    });
  }

  #cruxHandleEyeballVariantSelection(target, selectedOption) {
    if (!this.#cruxIsEyeballThreeOptionPicker() || this.dataset.cruxEyeballNativeRepairing === 'true') return false;
    if (!(target instanceof HTMLElement)) return false;

    const lockedIndex = this.#cruxEyeballChangedIndex(target);
    const lockedValue = this.#cruxEyeballChangedValue(target, selectedOption);
    if (lockedIndex < 0 || !lockedValue) return false;

    const previousResource = this.#cruxEyeballCurrentResource();
    this.updateSelectedOption(target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    this.#cruxEyeballVariantAbortController?.abort();
    const controller = new AbortController();
    const requestId = ++this.#cruxEyeballVariantRequestId;
    this.#cruxEyeballVariantAbortController = controller;
    this.dataset.cruxEyeballNativeRepairing = 'loading';
    this.#cruxEyeballSetPurchaseBusy(true);

    const firstRequestUrl = this.buildRequestUrl(selectedOption);
    let purchaseStateValid = false;
    this.#cruxFetchEyeballSection(firstRequestUrl, controller.signal)
      .then(async (firstResult) => {
        if (controller.signal.aborted || requestId !== this.#cruxEyeballVariantRequestId) return null;
        if (this.#cruxEyeballResponseMatchesLock(firstResult.resource, lockedIndex, lockedValue)) return firstResult;

        const fallbackUrl = this.#cruxEyeballBuildFallbackRequest(firstResult.html, lockedIndex, lockedValue);
        if (!fallbackUrl) throw new Error('No purchasable variant matches the selected eye and gauge');
        return this.#cruxFetchEyeballSection(fallbackUrl, controller.signal);
      })
      .then((finalResult) => {
        if (!finalResult || controller.signal.aborted || requestId !== this.#cruxEyeballVariantRequestId) return;
        if (!this.#cruxEyeballResponseMatchesLock(finalResult.resource, lockedIndex, lockedValue)) {
          throw new Error('Resolved variant does not preserve the user selection');
        }
        this.#cruxCommitEyeballVariant(finalResult.html, finalResult.resource);
        purchaseStateValid = true;
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || requestId !== this.#cruxEyeballVariantRequestId) return;
        console.error(error);
        this.#cruxRestoreEyeballVariant(previousResource);
        purchaseStateValid = Boolean(previousResource?.id && previousResource?.available !== false);
      })
      .finally(() => {
        if (requestId !== this.#cruxEyeballVariantRequestId) return;
        this.#cruxEyeballVariantAbortController = undefined;
        delete this.dataset.cruxEyeballNativeRepairing;
        this.#cruxEyeballSetPurchaseBusy(false, purchaseStateValid);
      });

    return true;
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        const resource = JSON.parse(textContent);
        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          this.#cruxSyncMlpMediaAfterFeaturedMorph(resource, morphElementSelector);
          this.#cruxSyncEyeballMediaAfterFeaturedMorph(resource, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // DARK product forms listen on the stable section. Dispatching from a picker being morphed
        // can strand the add-to-cart form on the previous variant. AURA keeps the native target.
        const isDarkWorld = document.body?.dataset?.cruxWorld === 'dark';
        const selectedOptionValueId = isDarkWorld
          ? this.#cruxEyeballSelectedOptionValueIds(this).at(-1)
          : this.selectedOptionId;
        if (selectedOptionValueId) {
          if (isDarkWorld && resource?.id) this.#cruxEyeballSyncPurchaseVariant(resource.id);
          const variantUpdateTarget = isDarkWorld
            ? this.closest('.shopify-section, dialog, product-card') || this
            : this;
          variantUpdateTarget.dispatchEvent(
            new VariantUpdateEvent(resource, selectedOptionValueId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#cruxSortEyeballLengthOptions();
    this.syncUnavailableOptions();
    this.correctUnavailableSelection();
    this.updateVariantPickerCss();

    if (new URLSearchParams(window.location.search).has('color_jump_preview')) {
      const selectedOption = this.selectedOption;
      const focusedElement = this.contains(document.activeElement) ? document.activeElement : null;
      const focusedOptionValueId = focusedElement?.dataset?.optionValueId || selectedOption?.dataset?.optionValueId;
      const anchorBefore =
        (selectedOption instanceof HTMLElement ? selectedOption.closest('label, .variant-option') : null) || this;
      const anchorTop = anchorBefore.getBoundingClientRect().top;
      focusedElement?.blur?.();
      const selectedAfter = this.selectedOption;
      const anchorAfter =
        (selectedAfter instanceof HTMLElement ? selectedAfter.closest('label, .variant-option') : null) || this;
      const restoreViewport = () => {
        if (!document.documentElement.contains(this) || !document.documentElement.contains(anchorAfter)) return;
        const delta = anchorAfter.getBoundingClientRect().top - anchorTop;
        if (Math.abs(delta) > 1) window.scrollBy(0, delta);
      };
      const escapedOptionValueId = focusedOptionValueId && CSS.escape(focusedOptionValueId);
      const focusCandidate = escapedOptionValueId
        ? this.querySelector('[data-option-value-id="' + escapedOptionValueId + '"]')
        : null;
      const focusTarget = focusCandidate instanceof HTMLOptionElement ? focusCandidate.closest('select') : focusCandidate;
      focusTarget instanceof HTMLElement && focusTarget.focus({ preventScroll: true });
      restoreViewport();
      requestAnimationFrame(() => requestAnimationFrame(restoreViewport));
    }

    return newProduct;
  }

  /**
   * Returns whether a picker option cannot currently be purchased.
   * @param {Element} option - Radio input or dropdown option.
   * @returns {boolean}
   */
  isUnavailableOption(option) {
    if (!(option instanceof HTMLElement)) return false;
    return option.dataset.optionAvailable === 'false' || option.getAttribute('aria-disabled') === 'true';
  }

  /**
   * Makes unavailable choices truly non-interactive, while keeping them visible.
   */
  syncUnavailableOptions() {
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('fieldset input[data-option-available]')
    );

    for (const radio of radios) {
      const unavailable = this.isUnavailableOption(radio);
      radio.disabled = unavailable;
      radio.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      radio.closest('label')?.toggleAttribute('aria-disabled', unavailable);
    }

    const options = /** @type {NodeListOf<HTMLOptionElement>} */ (
      this.querySelectorAll('select option[data-option-available]')
    );

    for (const option of options) {
      option.disabled = this.isUnavailableOption(option);
    }
  }

  /**
   * If the current selection lands on an unavailable value after an option refresh,
   * move it to the first available value in that same option group.
   */
  correctUnavailableSelection() {
    const checkedUnavailableRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('fieldset input:checked[data-option-available="false"]')
    );

    if (checkedUnavailableRadio) {
      const replacement = /** @type {HTMLInputElement | null} */ (
        checkedUnavailableRadio
          .closest('fieldset')
          ?.querySelector('input[data-option-available="true"]:not(:disabled)')
      );

      if (replacement && replacement !== checkedUnavailableRadio) {
        requestAnimationFrame(() => replacement.click());
        return;
      }
    }

    const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (this.querySelectorAll('select'));
    for (const select of selects) {
      const selected = select.options[select.selectedIndex];
      if (!selected || !this.isUnavailableOption(selected)) continue;

      const replacement = Array.from(select.options).find((option) => !this.isUnavailableOption(option));
      if (replacement) {
        select.value = replacement.value;
        requestAnimationFrame(() => select.dispatchEvent(new Event('change', { bubbles: true })));
        return;
      }
    }
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
